import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { FIELD_KEYS } from "@shared/constants/fields";
import { screenByKey } from "@shared/interview/chapters";
import { boardCell, boardOptions, boardPickNotes, boardStep, boardTable, boardTiles } from "@shared/interview/board";
import { isChildRelationship } from "@shared/constants/dependent-relationships";
import { composeContactName } from "@shared/contact";
import type { GenericEntry } from "@shared/schema";
import { useSectionFields } from "./useSectionFields";
import { saveInBackground, useSaveFailure } from "./saveFailure";
import { TilePicker, type TileSelection } from "./TilePicker";
import { TileTables, type TiledRow } from "./TileTables";
import { ContactCard, LinkedPersonCell, cardSummary, type CardSlot } from "./PersonPicker";
import { ConsequencePicker, ConsequenceNote, consequenceFor, groupRowNotes } from "./ConsequencePicker";
import { OnePanelProvider, usePanels } from "./useOnePanelOpen";
import { isStillLookedAfter, PERSON_MARK } from "./CarriedInTiles";
import { DateCell, NoteCard } from "./InterviewCells";
import { queueRowSave, rowSaveKey } from "@/lib/rowSaveQueue";
import { BENEFICIARIES_SLUG } from "@shared/essentials-spec";
import { useSectionRows } from "./useSectionRows";
import { SecondLine } from "./SecondLine";

/**
 * s221 Lane 1 — CHILDREN AND DEPENDENTS, end to end (SPEC: `Main.dc.html` v289, the `kids` step).
 *
 * Two tiles and "Nobody depends on me"; the children table and, as its second table, the dependants
 * table (mechanism 3), both with the SPEC's shared column widths. Built only from the Lane 1 pieces.
 *
 * ## One section, a kind column (D-221.10)
 * Every row lives in `children-dependents-overview`. `person_kind` says Child or Dependant; a row typed
 * on the planner page before that field existed is classified from its `relationship` instead.
 *
 * ## Writes — the two shipped paths, no third (interview-write-paths.test.ts)
 *  • facts  → `POST /api/clients/:id/generic-entries`, per cell (D-210.18);
 *  • flags  → `PATCH /api/clients/:id/situation-flags` — `hasChildren` and `hasDependents`
 *    (ANSWER-FLAG-WRITE-1: the interview now writes 3 of the route's flags, up from 1).
 *
 * ## 🔴 Deselect HIDES, never deletes (D-210.18 part 3, G8)
 * Turning a tile off, "Nobody depends on me", and × all set `isHidden: true` on the row's entries
 * through the same upsert — no value is sent, so no value changes. Turning the tile back on unhides
 * the rows with everything they held. There is no DELETE anywhere in this file (test-enforced).
 *
 * ## 🔴 Gates (Phase 0 finding)
 * The guardian, health, care and school fields sit behind a per-row yes/no gate (`showIf`). A value
 * written behind a closed gate is saved but hidden on the plan's own row (rule 121 broken), so a write
 * to a gated field opens its gate first. `GATE_FOR_FIELD` is pinned to the spec's `showIf` by a test.
 */

export type PersonKind = "child" | "dependant";

export type ChildRow = {
  rowId: string;
  kind: PersonKind;
  /** Every entry on the row is hidden (deselected or removed). */
  hidden: boolean;
  /** Field key → stored value, including hidden entries, so an unhide restores them. */
  values: Record<string, string>;
};

/** A gated field → the yes/no gate that must be "true" for it to show. Pinned to the spec by test. */
export const GATE_FOR_FIELD: Readonly<Record<string, string>> = {
  [FIELD_KEYS.PRIMARY_GUARDIAN]: FIELD_KEYS.HAS_GUARDIANSHIP_INFO,
  [FIELD_KEYS.PRIMARY_GUARDIAN_PHONE]: FIELD_KEYS.HAS_GUARDIANSHIP_INFO,
  [FIELD_KEYS.PRIMARY_GUARDIAN_EMAIL]: FIELD_KEYS.HAS_GUARDIANSHIP_INFO,
  [FIELD_KEYS.PRIMARY_GUARDIAN_ADDRESS]: FIELD_KEYS.HAS_GUARDIANSHIP_INFO,
  [FIELD_KEYS.BACKUP_GUARDIAN]: FIELD_KEYS.HAS_GUARDIANSHIP_INFO,
  [FIELD_KEYS.BACKUP_GUARDIAN_PHONE]: FIELD_KEYS.HAS_GUARDIANSHIP_INFO,
  [FIELD_KEYS.BACKUP_GUARDIAN_EMAIL]: FIELD_KEYS.HAS_GUARDIANSHIP_INFO,
  [FIELD_KEYS.BACKUP_GUARDIAN_ADDRESS]: FIELD_KEYS.HAS_GUARDIANSHIP_INFO,
  [FIELD_KEYS.CONDITIONS]: FIELD_KEYS.HAS_HEALTH_INFO,
  [FIELD_KEYS.DOCTOR_NAME]: FIELD_KEYS.HAS_HEALTH_INFO,
  [FIELD_KEYS.DOCTOR_PHONE]: FIELD_KEYS.HAS_HEALTH_INFO,
  [FIELD_KEYS.DOCTOR_ADDRESS]: FIELD_KEYS.HAS_HEALTH_INFO,
  [FIELD_KEYS.DOCTOR_EMAIL]: FIELD_KEYS.HAS_HEALTH_INFO, // CONTACT-CARD-ONE-1
  [FIELD_KEYS.SCHOOL_PHONE]: FIELD_KEYS.HAS_EDUCATION_INFO,
  [FIELD_KEYS.SCHOOL_ADDRESS]: FIELD_KEYS.HAS_EDUCATION_INFO,
  [FIELD_KEYS.SCHOOL_CONTACT_PERSON]: FIELD_KEYS.HAS_EDUCATION_INFO,
  // CONTACT-CARD-ONE-1: the school card's new answers open the same gate.
  [FIELD_KEYS.SCHOOL_EMAIL]: FIELD_KEYS.HAS_EDUCATION_INFO,
  [FIELD_KEYS.SCHOOL_CONTACT_PHONE]: FIELD_KEYS.HAS_EDUCATION_INFO,
  [FIELD_KEYS.SCHOOL_CONTACT_EMAIL]: FIELD_KEYS.HAS_EDUCATION_INFO,
  [FIELD_KEYS.SCHOOL_CONTACT_ADDRESS]: FIELD_KEYS.HAS_EDUCATION_INFO,
  [FIELD_KEYS.ROUTINES]: FIELD_KEYS.HAS_CARE_INFO,
  [FIELD_KEYS.RAISED_IN_FAITH]: FIELD_KEYS.HAS_CARE_INFO,
  [FIELD_KEYS.CARE_NOTES]: FIELD_KEYS.HAS_CARE_INFO,
  [FIELD_KEYS.SCHOOL_NAME]: FIELD_KEYS.HAS_EDUCATION_INFO,
  [FIELD_KEYS.GRADE]: FIELD_KEYS.HAS_EDUCATION_INFO,
};

const KIND_VALUE: Record<PersonKind, string> = { child: "Child", dependant: "Dependant" };

/** Child or dependant, from the kind column, else from the relationship. Pure. */
export function kindOf(values: Record<string, string>): PersonKind {
  const k = values[FIELD_KEYS.PERSON_KIND];
  if (k === KIND_VALUE.dependant) return "dependant";
  if (k === KIND_VALUE.child) return "child";
  const rel = values[FIELD_KEYS.RELATIONSHIP] ?? "";
  if (!rel || isChildRelationship(rel) || rel === "Stepchild") return "child";
  return "dependant";
}

/** Group a section's entries into rows. Pure; order is first-written first. */
export function buildChildRows(
  entries: readonly Pick<GenericEntry, "rowId" | "sectionId" | "fieldId" | "value" | "isHidden" | "createdAt">[],
  sectionId: string | undefined,
  keyByFieldId: ReadonlyMap<string, string>,
): ChildRow[] {
  if (!sectionId) return [];
  const byRow = new Map<string, { values: Record<string, string>; allHidden: boolean; first: number }>();
  for (const e of entries) {
    if (e.sectionId !== sectionId || !e.rowId) continue;
    const key = keyByFieldId.get(e.fieldId);
    if (!key) continue;
    const at = e.createdAt ? new Date(e.createdAt).getTime() : 0;
    const r = byRow.get(e.rowId) ?? { values: {}, allHidden: true, first: at };
    r.values[key] = e.value ?? "";
    r.allHidden = r.allHidden && e.isHidden === true;
    r.first = Math.min(r.first, at);
    byRow.set(e.rowId, r);
  }
  return Array.from(byRow.entries())
    .sort((a, b) => a[1].first - b[1].first || a[0].localeCompare(b[0]))
    .map(([rowId, r]) => ({ rowId, kind: kindOf(r.values), hidden: r.allHidden, values: r.values }));
}

/** A row names a person only once it carries a name. Pure. */
export function hasName(r: Pick<ChildRow, "values">): boolean {
  return composeContactName({ firstName: r.values[FIELD_KEYS.FIRST_NAME], surname: r.values[FIELD_KEYS.SURNAME] }) !== "";
}

/**
 * 🔴 s221 Lane 1c, fix 7 — BLANK ROWS ARE NOT PEOPLE. The rows one table draws. Pure.
 *  • A table with named rows draws those rows — and any row the planner added THIS visit (`createdHere`,
 *    still waiting for its name) — and no other blank one. A nameless row loaded from the plan (a planner-
 *    page scaffold, a tap that was never named) is not drawn above real people.
 *  • An EMPTY table IN USE (its tile picked, no named row of its kind) draws one blank starter row, if the
 *    plan holds one. A table whose tile is NOT picked draws no blank row at all — found on staging: a
 *    nameless dependant row sat in the dependants table while "Someone else depends on me" was off.
 * Nothing is deleted; a nameless row simply is not a person here, on this screen or any screen after it.
 */
export function rowsForTable(rows: readonly ChildRow[], kind: PersonKind, createdHere: ReadonlySet<string>, inUse = true): ChildRow[] {
  // s221 Lane 1d: rows render ONLY for a picked tile — picking "none" (or un-picking a tile) hides the
  // table outright, as the board's none.pick does. Nothing is deleted.
  if (!inUse) return [];
  const ofKind = rows.filter((r) => !r.hidden && r.kind === kind);
  const shown = ofKind.filter((r) => hasName(r) || createdHere.has(r.rowId));
  if (shown.length > 0) return shown;
  const starter = ofKind[ofKind.length - 1];
  return starter ? [starter] : [];
}

/**
 * The ONE read-and-write surface for the children-and-dependants rows — shared by this screen and the
 * day-to-day screen (G7), so both write the same rows the same way.
 */
export function useChildRows(clientId: string, onAnswered: () => void) {
  const { categoryId, categoryType, sectionId, fieldIdByKey } = useSectionFields(
    clientId,
    CATEGORY_SLUGS.CHILDREN_AND_DEPENDENTS,
    SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW,
  );
  const keyByFieldId = useMemo(() => {
    const m = new Map<string, string>();
    fieldIdByKey.forEach((id, key) => m.set(id, key));
    return m;
  }, [fieldIdByKey]);

  const entriesKey = ["/api/clients", clientId, "generic-entries", categoryType];
  const { data: entries, isSuccess: entriesLoaded } = useQuery<GenericEntry[]>({
    queryKey: entriesKey,
    queryFn: async () => (await apiRequest("GET", `/api/clients/${clientId}/generic-entries?categoryType=${categoryType}`)).json(),
    enabled: !!clientId && !!categoryType,
  });
  const rows = useMemo(() => buildChildRows(entries ?? [], sectionId, keyByFieldId), [entries, sectionId, keyByFieldId]);

  // s228 (INTERVIEW-SAVE-FAILURE): a refused save puts up the red pop-up (saveFailure.ts). The writers below still
  // throw, so a sequence stops at the first refusal and the box keeps what was typed.
  const onSaveFailed = useSaveFailure();
  // s236 (D-236.46): one row's requests go out one at a time — see useSectionRows and rowSaveQueue.
  const upsertMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      await queueRowSave(rowSaveKey(clientId, body.sectionId as string | undefined, body.rowId as string | undefined), () =>
        apiRequest("POST", `/api/clients/${clientId}/generic-entries`, body)),
    onError: onSaveFailed,
  });
  // s229 (D-229.68(b), D-229.84): a child's row is saved as its typed record in ONE request; hide / show
  // stay per cell. Class C-server-deduped: the record and cells upsert by row — once one fire has landed; two at once
  // on a NEW cell both inserted (s236, D-236.46), so a row's requests are queued (queueRowSave).
  const typedMutation = useMutation({
    mutationFn: async (body: { rowId: string; cells: Array<{ fieldId: string; value: string }> }) =>
      await queueRowSave(rowSaveKey(clientId, sectionId, body.rowId), () =>
        apiRequest("POST", `/api/clients/${clientId}/typed-rows`, { sectionId, categoryId, ...body })),
    onError: onSaveFailed,
  });
  /** Save a row's values, in order (a gate before the answers behind it), as one typed-row write. */
  const saveTyped = async (rowId: string, ordered: Array<[string, string]>) => {
    if (!sectionId || !categoryId) return;
    const cells = ordered.flatMap(([key, value]) => { const fieldId = fieldIdByKey.get(key); return fieldId ? [{ fieldId, value }] : []; });
    if (cells.length) await typedMutation.mutateAsync({ rowId, cells });
  };
  const flagMutation = useMutation({
    mutationFn: async (flags: { hasChildren?: boolean; hasDependents?: boolean }) =>
      await apiRequest("PATCH", `/api/clients/${clientId}/situation-flags`, flags),
    onError: onSaveFailed,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries"] });
    void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId] });
    onAnswered();
  };

  const post = async (rowId: string, key: string, patch: { value?: string; isHidden?: boolean }) => {
    const fieldId = fieldIdByKey.get(key);
    if (!fieldId || !sectionId || !categoryId) return;
    await upsertMutation.mutateAsync({ categoryId, sectionId, fieldId, rowId, ...patch });
  };

  /** Write one cell, opening its gate first if it sits behind a closed one. */
  const writeCell = async (rowId: string, key: string, value: string) => {
    const gate = GATE_FOR_FIELD[key];
    const row = rows.find((r) => r.rowId === rowId);
    const opens: Array<[string, string]> = gate && value.trim() && row?.values[gate] !== "true" ? [[gate, "true"]] : [];
    await saveTyped(rowId, [...opens, [key, value]]);
    refresh();
  };

  /** Write several cells of one row (a contact card's Done): each closed gate opens ONCE, then every
   *  field is written, then one refresh — never the same gate twice off a stale row. */
  const writeCells = async (rowId: string, changes: Record<string, string>) => {
    const row = rows.find((r) => r.rowId === rowId);
    const gates = new Set<string>();
    for (const [key, value] of Object.entries(changes)) {
      const gate = GATE_FOR_FIELD[key];
      if (gate && value.trim() && row?.values[gate] !== "true") gates.add(gate);
    }
    await saveTyped(rowId, [...Array.from(gates).map((g): [string, string] => [g, "true"]), ...Object.entries(changes)]);
    refresh();
  };

  /** Hide or bring back every entry on a row. Sends NO value, so nothing it held is changed. */
  const setRowHidden = async (rowId: string, hidden: boolean) => {
    const row = rows.find((r) => r.rowId === rowId);
    if (!row) return;
    for (const key of Object.keys(row.values)) await post(rowId, key, { isHidden: hidden });
  };

  /** A new row of a kind. The tap is the answer (D-200.2): the row exists as soon as it is asked for. */
  const createRow = async (kind: PersonKind) => {
    const rowId = crypto.randomUUID();
    await saveTyped(rowId, [[FIELD_KEYS.PERSON_KIND, KIND_VALUE[kind]]]);
    refresh();
    return rowId;
  };

  return {
    // 🔴 s221 staging look: `ready` must wait for the FIELDS too. Rows are keyed by field, so with
    // entries loaded but fields not, every row reads empty — and both screens seed their tile
    // selection once, on the first ready render, from those empty rows (tiles stuck unpicked).
    ready: !!sectionId && !!categoryId && entriesLoaded && fieldIdByKey.size > 0,
    rows,
    fieldIdByKey,
    writeCell,
    writeCells,
    setRowHidden,
    createRow,
    setFlags: (flags: { hasChildren?: boolean; hasDependents?: boolean }) => flagMutation.mutateAsync(flags),
    refresh,
  };
}

// ── SPEC copy — read from the board (s222 BOARD-AS-SPEC-1, D-221.40) ─────────────────────────────
// Everything the board carries for the `kids` step comes from the generated board data; wording → stored
// value goes through board-map.ts. `npm run check:board-parity` fails the build when the two disagree.

const KIDS = boardStep("kids");
const KIDS_TILES = boardTiles(KIDS);
const TILES = KIDS_TILES.tiles.map(({ id, label, sub, mark, color }) => ({ id, label, sub, mark, color }));
/** s226 (board v333) — a row's lead is its tile's `full`, read from the board (RULE 137): "Child", and "Who" for a
 *  dependant (it was typed here as "Dependant" and drifted when the board changed it). */
export const LEAD_OF: Readonly<Record<"child" | "dep", string>> = {
  child: KIDS_TILES.tiles.find((t) => t.id === "child")?.full ?? "",
  dep: KIDS_TILES.tiles.find((t) => t.id === "dep")?.full ?? "",
};
const TILE_KIND: Record<string, PersonKind> = { child: "child", dep: "dependant" };
const FLAG_FOR: Record<PersonKind, "hasChildren" | "hasDependents"> = { child: "hasChildren", dependant: "hasDependents" };

const NOTE_FOR: Record<PersonKind, string> = { child: KIDS.noteFor?.child ?? "", dependant: KIDS.noteFor?.dep ?? "" };

// The board's cells, by their board keys (`k`).
/** s236 (D-236.87): Under 18?'s answers — the Beneficiaries field's own stored values (the ONE answer lives there). */
const CHILD_MINOR_OPTIONS = boardOptions("benef", boardCell(boardStep("benef"), "minor"));

const CELL = {
  who: boardCell(KIDS, "who"), crel: boardCell(KIDS, "crel"), guard: boardCell(KIDS, "guard"), inwill: boardCell(KIDS, "inwill"),
  dwho: boardCell(KIDS, "dwho"), drel: boardCell(KIDS, "drel"), dguard: boardCell(KIDS, "dguard"),
  dob: boardCell(KIDS, "dob"),
};

/** s221 Lane 1c, fix 9: the board's words (`crel` / `drel`) over the STORED shared choices
 *  (dependent-relationships.ts) — the gov-form child list and every reader keep reading the same values. */
export const CHILD_RELATIONSHIPS = boardOptions("kids", CELL.crel);
export const DEPENDANT_RELATIONSHIPS = boardOptions("kids", CELL.drel);
const IN_WILL_OPTIONS = boardOptions("kids", CELL.inwill);

/** The board's pick notes, keyed by STORED value. */
export const IN_WILL_NOTES = boardPickNotes("kids", "inwill");
const RELATIONSHIP_NOTES = boardPickNotes("kids", "crel");

export const CHILD_TABLE = boardTable(KIDS);
export const DEPENDANT_TABLE = { ...boardTable(KIDS.second ?? {}), tiles: KIDS.second?.tiles ?? [] };

// ── the screen ────────────────────────────────────────────────────────────────────────────────────

export function ChildrenScreen({ clientId, onAnswered, today }: { clientId: string; onAnswered: () => void; today?: string }) {
  return (
    <OnePanelProvider>
      <ChildrenScreenBody clientId={clientId} onAnswered={onAnswered} today={today ?? new Date().toISOString().slice(0, 10)} />
    </OnePanelProvider>
  );
}

function ChildrenScreenBody({ clientId, onAnswered, today }: { clientId: string; onAnswered: () => void; today: string }) {
  const data = useChildRows(clientId, onAnswered);
  // s236 (D-236.87, D-236.40(3)): a child's "Under 18?" with no date of birth is the ONE answer on the child's linked
  // Beneficiaries row (same row id) — read and written there, so both pages show the one value.
  const benef = useSectionRows(clientId, CATEGORY_SLUGS.LEGAL, BENEFICIARIES_SLUG, onAnswered);
  const { rows, fieldIdByKey } = data;
  const panels = usePanels();
  // Creating a row is Class-A (a second fire is a second row), so every path that creates one goes
  // through ONE lock (D-169.10). Toggles and cell writes are last-wins and need none.
  const createFlight = useSingleFlight();
  // Rows added THIS visit stay drawn while they wait for a name (fix 7).
  const [createdHere, setCreatedHere] = useState<ReadonlySet<string>>(() => new Set());
  const create = (kind: PersonKind) =>
    createFlight.run(() => data.createRow(kind)).then((id) => { if (id) setCreatedHere((s) => new Set(s).add(id)); });

  const { data: client } = useQuery<{ hasChildren?: boolean | null; hasDependents?: boolean | null }>({
    queryKey: ["/api/clients", clientId],
    enabled: !!clientId,
  });

  const visible = rows.filter((r) => !r.hidden);
  const [selection, setSelection] = useState<TileSelection | null>(null);
  // The selection is seeded ONCE from what the plan already holds, then owned here — a refetch
  // mid-answer must not flip a tile the planner just pressed.
  useEffect(() => {
    if (selection || !data.ready || client === undefined) return;
    // Fix 7: only a NAMED row says "I have children" — a blank row never picks a tile.
    const selected = (["child", "dep"] as const).filter((t) => visible.some((r) => r.kind === TILE_KIND[t] && hasName(r)));
    const none = selected.length === 0 && client?.hasChildren === false && client?.hasDependents === false;
    setSelection({ selected: [...selected], none });
  }, [selection, data.ready, client, visible]);

  const sel = selection ?? { selected: [], none: false };

  /** Turn a kind on: flag true, bring back its hidden rows, or start its first row. */
  const turnOn = async (kind: PersonKind) => {
    await data.setFlags({ [FLAG_FOR[kind]]: true });
    const ofKind = rows.filter((r) => r.kind === kind);
    if (ofKind.some((r) => r.hidden)) {
      for (const r of ofKind.filter((x) => x.hidden)) await data.setRowHidden(r.rowId, false);
      data.refresh();
    } else if (ofKind.length === 0) {
      await create(kind);
    } else {
      data.refresh();
    }
  };

  /** Turn a kind off: flag false, hide its rows (never delete). */
  const turnOff = async (kind: PersonKind) => {
    await data.setFlags({ [FLAG_FOR[kind]]: false });
    for (const r of rows.filter((x) => x.kind === kind && !x.hidden)) await data.setRowHidden(r.rowId, true);
    data.refresh();
  };

  const onTiles = async (next: TileSelection, changed: string) => {
    setSelection(next);
    if (changed === "none") {
      if (!next.none) return;
      await data.setFlags({ hasChildren: false, hasDependents: false });
      for (const r of visible) await data.setRowHidden(r.rowId, true);
      data.refresh();
      return;
    }
    const kind = TILE_KIND[changed];
    if (next.selected.includes(changed)) await turnOn(kind);
    else await turnOff(kind);
  };

  const addRow = (rowId: string) => {
    const kind = rows.find((r) => r.rowId === rowId)?.kind ?? "child";
    saveInBackground(create(kind));
  };

  // 🔴 s221 Lane 1d (G8): × HIDES the row (never deletes) and leaves "Name removed · Undo" in its place
  // for the rest of this visit — the planner must never believe a person vanished from their plan.
  const [removedHere, setRemovedHere] = useState<ReadonlyMap<string, string>>(() => new Map());
  const removeRow = async (rowId: string) => {
    const r = rows.find((x) => x.rowId === rowId);
    const name = r ? composeContactName({ firstName: r.values[FIELD_KEYS.FIRST_NAME], surname: r.values[FIELD_KEYS.SURNAME] }) : "";
    setRemovedHere((m) => new Map(m).set(rowId, name || (r?.kind === "dependant" ? "This person" : "This child")));
    try {
      await data.setRowHidden(rowId, true);
    } catch (err) {
      // s228 (ruling 4): a refused × puts the row back — never "… removed · Undo" for a removal that did not happen.
      setRemovedHere((m) => { const n = new Map(m); n.delete(rowId); return n; });
      throw err;
    }
    data.refresh();
  };
  const undoRemove = async (rowId: string) => {
    await data.setRowHidden(rowId, false);
    setRemovedHere((m) => { const n = new Map(m); n.delete(rowId); return n; });
    data.refresh();
  };
  /** Named rows of a kind hidden on an EARLIER visit (not the ones undoable here) — the notice counts them. */
  const hiddenEarlier = (kind: PersonKind) => rows.filter((r) => r.kind === kind && r.hidden && hasName(r) && !removedHere.has(r.rowId));
  const bringBack = async (kind: PersonKind) => {
    for (const r of hiddenEarlier(kind)) await data.setRowHidden(r.rowId, false);
    data.refresh();
  };
  const noticeFor = (kind: PersonKind, inUse: boolean) =>
    inUse ? { count: hiddenEarlier(kind).length, noun: (kind === "child" ? ["child", "children"] : ["person", "people"]) as [string, string], onRestore: () => saveInBackground(bringBack(kind)) } : undefined;

  // Fix 7: each table draws only the rows `rowsForTable` allows — never a blank row above real people.
  // Removed-this-visit rows keep their place (as their undo line), in the plan's own row order.
  const withRemoved = (kind: PersonKind, inUse: boolean) => {
    if (!inUse) return [];
    const shown = new Set(rowsForTable(rows, kind, createdHere, true).map((r) => r.rowId));
    return rows.filter((r) => r.kind === kind && (shown.has(r.rowId) || removedHere.has(r.rowId)));
  };
  const childInUse = sel.selected.includes("child");
  const depInUse = sel.selected.includes("dep");
  const drawn = [...withRemoved("child", childInUse), ...withRemoved("dependant", depInUse)];

  // s234 (D-234.94): a note that is IDENTICAL on several rows of one table shows once under that table, naming the rows.
  const kidName = (r: ChildRow) => (r.values[FIELD_KEYS.FIRST_NAME] ?? "").trim()
    || composeContactName({ firstName: r.values[FIELD_KEYS.FIRST_NAME], surname: r.values[FIELD_KEYS.SURNAME] })
    || (r.kind === "dependant" ? "This person" : "This child");
  const notesOf = (kind: PersonKind) => {
    const rs = drawn.filter((r) => r.kind === kind && !removedHere.has(r.rowId));
    const rel = groupRowNotes(rs.map((r) => ({ rowId: r.rowId, name: kidName(r), text: consequenceFor(RELATIONSHIP_NOTES, r.values[FIELD_KEYS.RELATIONSHIP] ?? "") })));
    const inWill = kind === "child"
      ? groupRowNotes(rs.map((r) => ({ rowId: r.rowId, name: kidName(r), text: consequenceFor(IN_WILL_NOTES, r.values[FIELD_KEYS.GUARDIAN_NAMED_IN_WILL] ?? "") })))
      : { shared: [], ownRow: new Set<string>() };
    return { notes: [...rel.shared, ...inWill.shared], rel: new Set(rel.shared.flatMap((n) => n.rowIds)), inWill: new Set(inWill.shared.flatMap((n) => n.rowIds)) };
  };
  const childNotes = notesOf("child");
  const depNotes = notesOf("dependant");

  const tiled: TiledRow[] = drawn.map((r) => {
    const v = r.values;
    const isChild = r.kind === "child";
    // The undo line shows the MOMENT × is pressed — never a beat where the row looks untouched or gone.
    if (removedHere.has(r.rowId)) {
      return { rowId: r.rowId, tileId: isChild ? "child" : "dep", lead: isChild ? LEAD_OF.child : LEAD_OF.dep, cells: [],
        removed: { label: removedHere.get(r.rowId)!, onUndo: () => saveInBackground(undoRemove(r.rowId)) } };
    }
    const tid = (what: string) => `interview-kids-${what}-${r.rowId}`;
    const whoKey = `${r.rowId}:who`;
    const guardKey = `${r.rowId}:guardian`;
    const noteKey = `${r.rowId}:note`;
    // A child under 18, or with no date of birth (the plan cannot tell), gets a backup guardian.
    const withBackup = isChild && isStillLookedAfter({ kind: "child", dob: v[FIELD_KEYS.DOB] }, today);
    const relOptions = isChild ? CHILD_RELATIONSHIPS : DEPENDANT_RELATIONSHIPS;
    // The board's cell declarations for this row's table: children `who crel guard`, dependants `dwho drel dguard`.
    const whoCell = isChild ? CELL.who : CELL.dwho;
    const relCell = isChild ? CELL.crel : CELL.drel;
    const guardCell = isChild ? CELL.guard : CELL.dguard;
    // Each cell's accessible name is its column's header on the board.
    const table = isChild ? CHILD_TABLE : DEPENDANT_TABLE;
    const headerOf = (k: string) => table.columns.find((c) => c.key === k)?.label ?? "";
    const save = (changes: Record<string, string>) => data.writeCells(r.rowId, changes);

    // Fix 1 — `who` / `dwho`: the contact control. The person IS this row, so the name lands on the row's
    // own name parts. CONTACT-CARD-ONE-1: the same four answers as every other card — the person's own
    // mobile, email and address (a disabled adult dependant may not live at the planner's address).
    const whoSlots: CardSlot[] = [{
      label: "Person 1", name: { kind: "nameParts", first: FIELD_KEYS.FIRST_NAME, last: FIELD_KEYS.SURNAME },
      phone: FIELD_KEYS.PERSON_PHONE, email: FIELD_KEYS.PERSON_EMAIL, address: FIELD_KEYS.PERSON_ADDRESS,
    }];
    // Fix 1 — `guard` / `dguard`: the guardian (and the backup, the card's second person — s212) with the
    // phone, email and address the row keeps for each.
    const guardianSlot = (label: string): CardSlot => ({
      label, name: { kind: "contact", key: FIELD_KEYS.PRIMARY_GUARDIAN },
      phone: FIELD_KEYS.PRIMARY_GUARDIAN_PHONE, email: FIELD_KEYS.PRIMARY_GUARDIAN_EMAIL, address: FIELD_KEYS.PRIMARY_GUARDIAN_ADDRESS,
    });
    const backupSlot = (label: string): CardSlot => ({
      label, name: { kind: "contact", key: FIELD_KEYS.BACKUP_GUARDIAN },
      phone: FIELD_KEYS.BACKUP_GUARDIAN_PHONE, email: FIELD_KEYS.BACKUP_GUARDIAN_EMAIL, address: FIELD_KEYS.BACKUP_GUARDIAN_ADDRESS,
    });
    const guardSlots: CardSlot[] = isChild
      ? (withBackup ? [guardianSlot("Guardian"), backupSlot("Backup guardian")] : [guardianSlot("Guardian")])
      : [guardianSlot("Person 1"), backupSlot("Person 2")];

    const cells = [
      <LinkedPersonCell key="name" value="" label={cardSummary(whoSlots, v)} word={whoCell.word ?? ""}
        open={panels.isOpen(whoKey)} onOpen={() => panels.toggle(whoKey)} testId={tid("name")} />,
      <DateCell key="dob" value={v[FIELD_KEYS.DOB] ?? ""} testId={tid("dob")} ariaLabel={headerOf(isChild ? "dob" : "ddob")} onCommit={(d) => saveInBackground(data.writeCell(r.rowId, FIELD_KEYS.DOB, d))} />,
      <ConsequencePicker key="rel" value={v[FIELD_KEYS.RELATIONSHIP] ?? ""} options={relOptions} placeholder={relCell.ph} testId={tid("rel")} ariaLabel={headerOf(relCell.k)}
        onCommit={(val) => saveInBackground(data.writeCell(r.rowId, FIELD_KEYS.RELATIONSHIP, val))} />,
      <LinkedPersonCell key="guard" value="" label={cardSummary(guardSlots, v)} word={guardCell.word ?? ""}
        open={panels.isOpen(guardKey)} onOpen={() => panels.toggle(guardKey)} testId={tid("guardian")} />,
      ...(isChild
        ? [<ConsequencePicker key="inwill" value={v[FIELD_KEYS.GUARDIAN_NAMED_IN_WILL] ?? ""} options={IN_WILL_OPTIONS} placeholder={CELL.inwill.ph} testId={tid("inwill")}
            ariaLabel={headerOf("inwill")} onCommit={(val) => saveInBackground(data.writeCell(r.rowId, FIELD_KEYS.GUARDIAN_NAMED_IN_WILL, val))} />]
        : []),
    ];

    // s236 (D-236.87): with no date of birth, a child's row asks "Under 18?" on its second line — the board's `emptyThen`.
    const minorQ = isChild && !(v[FIELD_KEYS.DOB] ?? "").trim() ? (CELL.dob.emptyThen ?? []).find((q) => q.k === "minor") : undefined;
    const minorValue = benef.rows.find((b) => b.rowId === r.rowId)?.values[FIELD_KEYS.BENEFICIARY_MINOR] ?? "";
    const secondLine = minorQ ? (
      <SecondLine testId={tid("line2")} items={[{
        key: "minor", label: minorQ.label,
        control: <ConsequencePicker value={minorValue} options={CHILD_MINOR_OPTIONS} placeholder={minorQ.ph} ariaLabel={minorQ.label} testId={tid("minor")}
          onCommit={(val) => saveInBackground(benef.writeCells(r.rowId, { [FIELD_KEYS.BENEFICIARY_MINOR]: val }))} />,
      }]} />
    ) : null;

    const whoCard = panels.isOpen(whoKey) ? (
      <ContactCard interviewLook key={whoKey} title={whoCell.title ?? ""} slots={whoSlots} values={v} fieldIdByKey={fieldIdByKey} clientId={clientId}
        undo={whoCell.undo} onSave={save} onClose={() => panels.close(whoKey)} testId={tid("who")} />
    ) : null;
    const guardianCard = panels.isOpen(guardKey) ? (
      <ContactCard interviewLook key={guardKey} title={guardCell.title ?? ""} slots={guardSlots} values={v}
        fieldIdByKey={fieldIdByKey} clientId={clientId} undo={guardCell.undo}
        moreLabel={isChild ? "+ Add a backup guardian" : "+ Add another person"}
        onSave={save} onClose={() => panels.close(guardKey)} testId={tid("guardian")} />
    ) : null;

    return {
      rowId: r.rowId,
      tileId: isChild ? "child" : "dep",
      lead: isChild ? LEAD_OF.child : LEAD_OF.dep,
      leadMark: isChild ? PERSON_MARK.child : PERSON_MARK.dependant,
      cells,
      canAddRow: true,
      note: {
        value: v[FIELD_KEYS.IDENTITY_NOTES] ?? "",
        open: panels.isOpen(noteKey),
        onToggle: () => panels.toggle(noteKey),
        // s237 (D-237.30): the save is returned, so the Note's Done waits for it.
        onCommit: (val) => (val !== (v[FIELD_KEYS.IDENTITY_NOTES] ?? "") ? data.writeCell(r.rowId, FIELD_KEYS.IDENTITY_NOTES, val) : undefined),
      },
      panels: (
        <>
          {secondLine}
          {whoCard}
          {guardianCard}
          {!(isChild ? childNotes : depNotes).rel.has(r.rowId) && <ConsequenceNote notes={RELATIONSHIP_NOTES} value={v[FIELD_KEYS.RELATIONSHIP] ?? ""} testId={tid("rel-note")} />}
          {isChild && !childNotes.inWill.has(r.rowId) && <ConsequenceNote notes={IN_WILL_NOTES} value={v[FIELD_KEYS.GUARDIAN_NAMED_IN_WILL] ?? ""} testId={tid("inwill-note")} />}
        </>
      ),
    };
  });

  // Honest failure: until the template carries the kind column (it converges on deploy, never on a
  // local boot), a tile would look picked while no row could be written. Say so instead.
  if (data.ready && !fieldIdByKey.has(FIELD_KEYS.PERSON_KIND)) {
    return (
      <p className="text-[14px] text-destructive" data-testid="interview-kids-not-ready">
        This part of your plan isn't ready yet. Please try again shortly.
      </p>
    );
  }

  return (
    <div data-testid="interview-kids">
      <TilePicker compact tiles={TILES} selection={sel} onChange={(n, c) => saveInBackground(onTiles(n, c))} none={screenByKey("kids")?.none} columns={KIDS_TILES.cols as 2 | 3 | 4 | 5} testIdPrefix="interview-kids" />
      <TileTables rows={tiled} first={{ ...CHILD_TABLE, hiddenNotice: noticeFor("child", childInUse) }}
        second={{ ...DEPENDANT_TABLE, hiddenNotice: noticeFor("dependant", depInUse) }} onAddRow={addRow} onRemove={(id) => saveInBackground(removeRow(id))} testIdPrefix="interview-kids-tables" cutLead rowMarks strongRowLine
        firstNotes={childNotes.notes} secondNotes={depNotes.notes} />
      {/* The board places each picked tile's note AFTER the tables, as a tinted card with its mark. */}
      <div className="mt-6 flex flex-col gap-1.5">
        {(["child", "dependant"] as const).map((k) => {
          const tileId = k === "child" ? "child" : "dep";
          return sel.selected.includes(tileId) ? (
            <NoteCard key={k} {...PERSON_MARK[k]} testId={`interview-kids-note-${k}`}>{NOTE_FOR[k]}</NoteCard>
          ) : null;
        })}
      </div>
    </div>
  );
}
